import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Car,
  ChevronRight,
  FileText,
  LayoutDashboard,
  ListTodo,
  Headset,
  Package,
  PanelLeftClose,
  Truck,
  Users,
} from 'lucide-react';
import { NavUser } from 'components/NavUser';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from 'components/ui/sidebar';
import {
  ROLE_DRIVER,
  hasPolesLot10Access,
  hasToolsAccess,
  isFuelRole,
  POLES_LOT10_HOME,
  FUEL_HOME,
  TOOLS_HOME,
} from 'services/authService';
import { SHOW_MATERIAL_INVENTORY } from '../config/featureFlags';

const LOGISTICS_LINKS = [
  { to: '/sagemcom/logistics/purchase-orders', label: 'Purchase Orders Tracking', dotColor: '#2563eb' },
  { to: '/sagemcom/logistics/invoice-tracking', label: 'Invoice Tracking', dotColor: '#7c3aed' },
  { to: '/sagemcom/logistics/delivery-tracking', label: 'Delivery Tracking', dotColor: '#059669' },
  { to: '/sagemcom/logistics/local-transportation-trucking', label: 'Local Transportation Trucking', dotColor: '#d97706' },
  ...(SHOW_MATERIAL_INVENTORY
    ? [{ to: '/sagemcom/logistics/material-inventory', label: 'Material Inventory', dotColor: '#0891b2' }]
    : []),
  { to: '/sagemcom/logistics/tools-inventory', label: 'Tools Inventory', dotColor: '#64748b' },
  { to: '/sagemcom/logistics/stationaries', label: 'Stationaries', dotColor: '#db2777' },
  { to: '/sagemcom/logistics/suppliers', label: 'Suppliers', dotColor: '#4f46e5' },
];

const FLEET_LINKS = [
  { to: '/sagemcom/fleet-management', label: 'Fleet Dashboard', dotColor: '#1e90ff' },
  { to: '/sagemcom/fleet-management/vehicle-report', label: 'Vehicle Report', dotColor: '#0ea5e9' },
  { to: '/sagemcom/fleet-management/fuel-report', label: 'Fuel Report', dotColor: '#f59e0b' },
  { to: '/sagemcom/fleet-management/payment-trucking', label: 'Fleet PO trucking', dotColor: '#10b981' },
  { to: '/sagemcom/fleet/car-rental-invoicing', label: 'Car Rental Invoicing', dotColor: '#8b5cf6' },
];

function CollapsibleGroup({ open, onOpenChange, label, icon: Icon, isActive, children }) {
  const { state, setOpen } = useSidebar();
  const collapsed = state === 'collapsed';

  if (collapsed) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={label}
          isActive={isActive}
          onClick={() => {
            setOpen(true);
            onOpenChange(true);
          }}
        >
          <Icon />
          <span>{label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={label} isActive={isActive || open}>
            <Icon />
            <span>{label}</span>
            <ChevronRight className="ml-auto shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>{children}</SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function SubNavLink({ to, label, pathname, dotColor }) {
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={pathname === to}>
        <Link to={to}>
          <span
            className="sidebar-sub-dot"
            style={{ '--sidebar-sub-dot-color': dotColor }}
            aria-hidden="true"
          />
          <span>{label}</span>
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

export default function AppSidebar({ authSession, onLogout }) {
  const { pathname } = useLocation();
  const { open, state, toggleSidebar } = useSidebar();
  const sidebarCollapsed = !open;

  const isDriverAccount = authSession?.role === ROLE_DRIVER;
  const isPolesLot10Account = hasPolesLot10Access(authSession);
  const isFuelAccount = isFuelRole(authSession);
  const isToolsAccount = hasToolsAccess(authSession);
  const isRestrictedAccount =
    isDriverAccount || authSession?.role === 'poles_lot10' || isFuelAccount;

  const [isLogisticsOpen, setIsLogisticsOpen] = useState(false);
  const [isFleetOpen, setIsFleetOpen] = useState(false);

  useEffect(() => {
    if (sidebarCollapsed) {
      setIsLogisticsOpen(false);
      setIsFleetOpen(false);
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (sidebarCollapsed) return;
    if (pathname.includes('/sagemcom/logistics')) {
      setIsLogisticsOpen(true);
      setIsFleetOpen(false);
    } else if (
      pathname.includes('/sagemcom/fleet-management') ||
      pathname.includes('/sagemcom/fleet/')
    ) {
      setIsFleetOpen(true);
      setIsLogisticsOpen(false);
    }
  }, [pathname, sidebarCollapsed]);

  return (
    <Sidebar collapsible="icon" className="app-shadcn-sidebar">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            {state === 'collapsed' ? (
              <SidebarMenuButton
                size="lg"
                tooltip="Expand sidebar"
                onClick={toggleSidebar}
                className="justify-center"
              >
                <div className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <Package className="size-4" />
                </div>
              </SidebarMenuButton>
            ) : (
              <div className="flex w-full items-center gap-1">
                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md p-2">
                  <div className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                    <Package className="size-4" />
                  </div>
                  <div className="sidebar-brand-meta grid min-w-0 flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">Sagemcom</span>
                    <span className="truncate text-xs text-sidebar-foreground/70">
                      Logistics Platform
                    </span>
                  </div>
                </div>
                <SidebarMenuButton
                  size="icon"
                  tooltip="Collapse sidebar"
                  onClick={toggleSidebar}
                  className="shrink-0"
                  aria-label="Collapse sidebar"
                >
                  <PanelLeftClose className="size-4" />
                </SidebarMenuButton>
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {!isRestrictedAccount && (
          <SidebarGroup>
            <SidebarGroupLabel>Platform</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu id="app-sidebar-nav">
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === '/'} tooltip="Dashboard">
                    <Link to="/">
                      <LayoutDashboard />
                      <span>Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <CollapsibleGroup
                  open={isLogisticsOpen}
                  onOpenChange={setIsLogisticsOpen}
                  label="Logistics"
                  icon={Box}
                  isActive={pathname.includes('/sagemcom/logistics')}
                >
                  {LOGISTICS_LINKS.map((link) => (
                    <SubNavLink key={link.to} {...link} pathname={pathname} />
                  ))}
                </CollapsibleGroup>

                <CollapsibleGroup
                  open={isFleetOpen}
                  onOpenChange={setIsFleetOpen}
                  label="Fleet Management"
                  icon={Car}
                  isActive={
                    pathname.includes('/sagemcom/fleet-management') ||
                    pathname.includes('/sagemcom/fleet/')
                  }
                >
                  {FLEET_LINKS.map((link) => (
                    <SubNavLink key={link.to} {...link} pathname={pathname} />
                  ))}
                </CollapsibleGroup>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === '/team'} tooltip="Team">
                    <Link to="/team">
                      <Users />
                      <span>Team</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === '/task-management'}
                    tooltip="Task Management"
                  >
                    <Link to="/task-management">
                      <ListTodo />
                      <span>Task Management</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === '/customer-service-kenya'}
                    tooltip="Customer Services Kenya"
                  >
                    <Link to="/customer-service-kenya">
                      <Headset />
                      <span>Customer Services Kenya</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {isRestrictedAccount && (
          <SidebarGroup>
            <SidebarGroupLabel>Menu</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu id="app-sidebar-nav">
                {isDriverAccount && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === '/sagemcom/fleet-management'}
                      tooltip="Fleet Dashboard"
                    >
                      <Link to="/sagemcom/fleet-management?mode=drivers">
                        <Car />
                        <span>Fleet Dashboard</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}

                {isPolesLot10Account && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === POLES_LOT10_HOME}
                      tooltip="Poles LOT 10"
                    >
                      <Link to={POLES_LOT10_HOME}>
                        <Truck />
                        <span>Poles LOT 10</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}

                {isToolsAccount && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === TOOLS_HOME}
                      tooltip="Tools Inventory"
                    >
                      <Link to={TOOLS_HOME}>
                        <Box />
                        <span>Tools Inventory</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}

                {isFuelAccount && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === FUEL_HOME}
                      tooltip="Fuel Report"
                    >
                      <Link to={FUEL_HOME}>
                        <FileText />
                        <span>Fuel Report</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <NavUser user={authSession} onLogout={onLogout} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
